import { alphabeticalCompare } from '@cims-online/shared'
import type { NotificationConditionType } from '@cims-online/shared'
import { Form } from 'antd'
import CIMSSelect from 'components/CIMSSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import type {
  CIMSSelectProps,
  LabeledValueType,
} from 'components/CIMSSelect/types'
import type { NotificationField } from '../../../../../types'
import { approvalFieldSettings } from '../../../../../utils/approvalFieldSettings'
import type { NotificationFormValues } from '../../../types'

type ApprovalConditionType = Exclude<
  NotificationConditionType['type'],
  'status_includes'
>

type ConditionOption = {
  deleted: boolean
  field?: LabeledValueType
  fieldId?: string
  label: string
  tooltip: string
  type: NotificationConditionType['type']
  value: string
}

type ConditionTypeSelectProps = Omit<
  CIMSSelectProps,
  'onChange' | 'options' | 'placeholder' | 'simple' | 'value'
> & {
  conditionName: number
  fields: NotificationField[]
  onChange?: (value: NotificationConditionType['type'] | undefined) => void
  value?: NotificationConditionType['type']
}

const APPROVAL_CONDITION_TYPES: ApprovalConditionType[] = [
  'includes_options',
  'all_option_states_filled',
  'any_option_state_empty',
]

const CONDITION_DESCRIPTIONS: Record<
  NotificationConditionType['type'],
  string
> = {
  all_option_states_filled:
    'Every option selected on the record has an approval state',
  any_option_state_empty:
    'At least one option selected on the record does not have an approval state',
  includes_options:
    'The approvals field contains at least one option configured in this condition',
  status_includes:
    "The record's current status matches one configured in this condition",
}

function approvalConditionLabel(
  field: NotificationField,
  type: ApprovalConditionType
) {
  const { plural, single, singular } = approvalFieldSettings(field)

  if (type === 'includes_options') {
    const fieldLabel = /\bfield$/i.test(field.name)
      ? field.name
      : `${field.name} field`

    return single
      ? `${field.name} is one of the selected ${plural}`
      : `${fieldLabel} contains at least one selected ${singular}`
  }

  if (type === 'all_option_states_filled')
    return `All ${singular} states are filled`

  return `Any ${singular} state is empty`
}

function approvalConditionValue(fieldId: string, type: ApprovalConditionType) {
  return `${type}:${fieldId}`
}

function conditionOptions(fields: NotificationField[]): ConditionOption[] {
  const approvalOptions = fields
    .filter((field) => field.type === 'approvals')
    .sort(
      (a, b) =>
        alphabeticalCompare(a.name, b.name) || alphabeticalCompare(a.id, b.id)
    )
    .flatMap((field) =>
      APPROVAL_CONDITION_TYPES.map((type) => ({
        fieldId: field.id,
        deleted: field.deleted,
        field: {
          deleted: field.deleted,
          label: field.name,
          tooltip: field.tooltip,
          value: field.id,
        },
        label: approvalConditionLabel(field, type),
        tooltip: CONDITION_DESCRIPTIONS[type],
        type,
        value: approvalConditionValue(field.id, type),
      }))
    )

  return [
    ...approvalOptions,
    {
      label: 'Status Includes',
      deleted: false,
      tooltip: CONDITION_DESCRIPTIONS.status_includes,
      type: 'status_includes',
      value: 'status_includes',
    },
  ]
}

/**
 * Presents field-specific condition choices while storing the persisted
 * condition type and field ID as separate form drafts.
 *
 * Option selections are cleared only when the target approvals field changes.
 */
function ConditionTypeSelect({
  conditionName,
  fields,
  onChange,
  value,
  ...selectProps
}: ConditionTypeSelectProps) {
  const form = Form.useFormInstance<NotificationFormValues>()
  const conditions = Form.useWatch('conditions', form)
  const field = conditions?.[conditionName]?.field
  const fieldId = field?.value.toString()
  const items = conditionOptions(fields)
  const selectedItem = items.find(
    (option) =>
      option.type === value &&
      (option.type === 'status_includes' || option.fieldId === fieldId)
  )
  const renderedOptions = useOptions({
    items,
    deleted: ({ deleted }) => deleted,
    label: 'label',
    sort: false,
    tooltip: 'tooltip',
    value: 'value',
  })
  const activeValues = new Set(
    items.filter(({ deleted }) => !deleted).map(({ value }) => value)
  )
  const options = renderedOptions.filter(({ value: optionValue }) =>
    optionValue == null ? false : activeValues.has(optionValue.toString())
  )
  const selectedOption = renderedOptions.find(
    ({ value: optionValue }) => optionValue === selectedItem?.value
  )
  const selectedValue: LabeledValueType | undefined = selectedItem
    ? {
        deleted: selectedItem.deleted,
        label: selectedOption?.label ?? selectedItem.label,
        tooltip: selectedItem.tooltip,
        value: selectedItem.value,
      }
    : undefined

  return (
    <CIMSSelect
      {...selectProps}
      options={options}
      placeholder="Select a condition"
      value={selectedValue}
      onChange={(selected) => {
        if (selected === undefined) {
          onChange?.(undefined)
          return
        }
        if (Array.isArray(selected) || typeof selected !== 'object') return
        const selectedValue = selected.value.toString()
        const option = items.find(({ value }) => value === selectedValue)
        if (!option) return

        if (option.field) {
          if (fieldId !== option.field.value.toString())
            form.setFieldValue(['conditions', conditionName, 'options'], [])
          form.setFieldValue(
            ['conditions', conditionName, 'field'],
            option.field
          )
        }
        onChange?.(option.type)
      }}
    />
  )
}

export default ConditionTypeSelect
